import { useTranslation } from 'react-i18next';
import type { Pagination, PaginationItem } from 'types/capi/plp';

import ArrowIcon from '~/assets/icons/Arrow';
import Tracked from '~/components/Tracked';

type Props = {
  pagination: Pagination;
};

type PageLinkItem = PaginationItem & { path: string };

type NavigationProps = {
  direction: 'next' | 'previous';
  item: PaginationItem;
  label: string;
};

export default function Pagination({ pagination }: Props) {
  const { t } = useTranslation('plp');
  const { next_page, pages, previous_page } = pagination;

  if (!pages?.length && !previous_page && !next_page) return null;

  const isOnlyNavigation = !previous_page || !next_page;
  const NavigationLink = isOnlyNavigation
    ? LabelledNavigationLink
    : IconNavigationLink;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 py-4 lg:gap-x-6 lg:py-8">
      {previous_page && (
        <NavigationLink
          direction="previous"
          item={previous_page}
          label={t('previous')}
        />
      )}
      <div className="flex shrink-0 items-center gap-2">
        {pages?.map((page, idx) =>
          isPageLink(page) ? (
            page.active ? (
              <CurrentPageLink key={page.path} item={page} />
            ) : (
              <PageLink key={page.path} item={page} />
            )
          ) : (
            <Ellipsis key={`ellipsis-${idx}`} />
          )
        )}
      </div>
      {next_page && (
        <NavigationLink direction="next" item={next_page} label={t('next')} />
      )}
    </div>
  );
}

function isPageLink(item: PaginationItem): item is PageLinkItem {
  return Boolean(item.path);
}

function NavigationArrow({ direction }: { direction: 'next' | 'previous' }) {
  return (
    <ArrowIcon
      className={direction === 'next' ? 'rotate-180' : undefined}
      fill="currentColor"
      height="16"
      width="16"
    />
  );
}

function LabelledNavigationLink({ direction, item, label }: NavigationProps) {
  return (
    <Tracked.InternalLink
      aria-label={label}
      className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-full border border-grey-divide px-4 text-xs leading-5 tracking-[0.24px] text-grey-hide hover:opacity-70 lg:min-w-[100px]"
      data-cy="pagination-button"
      name={`PAGINATION_PAGE_${item.text?.toUpperCase()}`}
      pagePosition="Body"
      to={item.path ?? ''}
      variant="none"
    >
      {direction === 'previous' && <NavigationArrow direction={direction} />}
      <span>{label}</span>
      {direction === 'next' && <NavigationArrow direction={direction} />}
    </Tracked.InternalLink>
  );
}

function IconNavigationLink({ direction, item, label }: NavigationProps) {
  return (
    <Tracked.InternalLink
      aria-label={label}
      className="flex size-10 shrink-0 items-center justify-center rounded-full border border-grey-divide text-xs leading-5 tracking-[0.24px] text-grey-hide hover:opacity-70 lg:w-auto lg:min-w-[100px] lg:gap-2 lg:px-4"
      data-cy="pagination-button"
      name={`PAGINATION_PAGE_${item.text?.toUpperCase()}`}
      pagePosition="Body"
      to={item.path ?? ''}
      variant="none"
    >
      {direction === 'previous' && <NavigationArrow direction={direction} />}
      <span className="hidden lg:block">{label}</span>
      {direction === 'next' && <NavigationArrow direction={direction} />}
    </Tracked.InternalLink>
  );
}

function PageLink({ item }: { item: PageLinkItem }) {
  return (
    <Tracked.InternalLink
      className="flex size-10 shrink-0 items-center justify-center rounded-full border border-grey-divide text-xs leading-5 tracking-[0.24px] text-grey-hide hover:opacity-70"
      data-cy="page-button"
      name={`PAGINATION_PAGE_${item.text?.toUpperCase()}`}
      pagePosition="Body"
      to={item.path}
      variant="none"
    >
      {item.text}
    </Tracked.InternalLink>
  );
}

function CurrentPageLink({ item }: { item: PageLinkItem }) {
  return (
    <Tracked.InternalLink
      className="flex size-10 shrink-0 items-center justify-center rounded-full bg-black text-xs leading-5 tracking-[0.24px] text-white"
      data-cy="page-button"
      name={`PAGINATION_PAGE_${item.text?.toUpperCase()}`}
      pagePosition="Body"
      to={item.path}
      variant="none"
    >
      {item.text}
    </Tracked.InternalLink>
  );
}

function Ellipsis() {
  return (
    <span className="flex h-10 shrink-0 items-center justify-center font-heading text-base font-medium leading-5 tracking-[-0.64px] text-grey-hide">
      ...
    </span>
  );
}
